import type { graphql } from "./graphql"

export type ActivityType = (typeof ActivityType)[number]
export const ActivityType = [
  "SALE",
  "MINT",
  "TRANSFER",
  "LISTING",
  "OFFER",
  "COLLECTION_OFFER",
  "TRAIT_OFFER",
] as const satisfies ReturnType<typeof graphql.scalar<"ActivityType">>[]

export type OfferType = (typeof OfferType)[number]
export const OfferType = [
  "BASIC",
  "CRITERIA_COLLECTION",
  "CRITERIA_TRAIT",
] as const satisfies ReturnType<typeof graphql.scalar<"OrderType">>[]

export type ChainIdentifier = ReturnType<
  typeof graphql.scalar<"ChainIdentifier">
>
export const ChainIdentifier = [
  "ethereum",
  "base",
  "blast",
  "arbitrum",
  "polygon",
  "avalanche",
  "zora",
  "optimism",
] as const satisfies ChainIdentifier[]

// List of predefined chain identifiers that we want to optimise for by e.g. defining logos as react components etc.
export type PredefinedChainIdentifier =
  | "ethereum"
  | "base"
  | "blast"
  | "arbitrum"
  | "polygon"
  | "avalanche"
  | "zora"
  | "optimism"
  | "cryptopunks"
  | "solana"
  | "bera_chain"
  | "ape_chain"
  | "unichain"
  | "ronin"
  | "gunzilla"
  | "abstract"
  | "hyperevm"
  | "somnia"

export type MarketplaceIdentifier = ReturnType<
  typeof graphql.scalar<"MarketplaceIdentifier">
>

export type ContractStandard = ReturnType<
  typeof graphql.scalar<"ContractStandard">
>
export const ContractStandard = [
  "ERC721",
  "ERC1155",
  "CRYPTOPUNKS",
] as const satisfies ContractStandard[]

export type CategoryIdentifier = (typeof CategoryIdentifier)[number]

export const CategoryIdentifier = [
  "ART",
  "GAMING",
  "MEMBERSHIPS",
  "MUSIC",
  "PFPS",
  "PHOTOGRAPHY",
  "DOMAIN_NAMES",
  "SPORTS_COLLECTIBLES",
  "VIRTUAL_WORLDS",
  "UNSPECIFIED",
] as const satisfies ReturnType<typeof graphql.scalar<"CategoryIdentifier">>[]

export type TopCollectionsSortBy = (typeof TopCollectionsSortBy)[number]

export const TopCollectionsSortBy = [
  "FLOOR_PRICE",
  "TOP_OFFER",
  "ONE_HOUR_VOLUME",
  "ONE_MINUTE_SALES",
  "FIVE_MINUTE_SALES",
  "FIFTEEN_MINUTE_SALES",
  "ONE_HOUR_SALES",
  "ONE_MINUTE_FLOOR_PRICE_CHANGE",
  "FIVE_MINUTE_FLOOR_PRICE_CHANGE",
  "FIFTEEN_MINUTE_FLOOR_PRICE_CHANGE",
  "ONE_HOUR_FLOOR_PRICE_CHANGE",
  "ONE_MINUTE_VOLUME",
  "FIVE_MINUTE_VOLUME",
  "FIFTEEN_MINUTE_VOLUME",
  "ONE_DAY_VOLUME",
  "ONE_DAY_SALES",
  "ONE_DAY_FLOOR_PRICE_CHANGE",
  "SEVEN_DAYS_VOLUME",
  "SEVEN_DAYS_SALES",
  "SEVEN_DAYS_FLOOR_PRICE_CHANGE",
  "THIRTY_DAYS_VOLUME",
  "THIRTY_DAYS_SALES",
  "THIRTY_DAYS_FLOOR_PRICE_CHANGE",
  "VOLUME",
  "SALES",
] as const satisfies ReturnType<typeof graphql.scalar<"TopCollectionsSortBy">>[]

export type CurrenciesSortBy = (typeof CurrenciesSortBy)[number]

export const CurrenciesSortBy = [
  "MARKET_CAP",
  "PRICE",
  "ONE_DAY_VOLUME",
  "ONE_DAY_PRICE_CHANGE",
  "ONE_HOUR_PRICE_CHANGE",
  "SEVEN_DAY_PRICE_CHANGE",
  "FOURTEEN_DAY_PRICE_CHANGE",
  "THIRTY_DAY_PRICE_CHANGE",
  "TWO_HUNDRED_DAY_PRICE_CHANGE",
  "ONE_YEAR_PRICE_CHANGE",
  "USD_VALUE",
  "GAIN",
] as const satisfies ReturnType<typeof graphql.scalar<"CurrenciesSortBy">>[]

export type CurrencySlug = (typeof CurrencySlug)[number]

export const CurrencySlug = [
  "TRENDING",
  "TOP",
  "NEW",
] as const satisfies ReturnType<typeof graphql.scalar<"CurrencySlug">>[]

export type CollectionItemsSortBy = (typeof CollectionItemsSortBy)[number]
export const CollectionItemsSortBy = [
  "CREATED_DATE",
  "LISTING_CREATED_DATE",
  "PRICE",
  "RARITY",
  "LAST_SALE",
  "TOP_OFFER",
  "RECEIVED_DATE",
  "LAST_SALE_DATE",
] as const satisfies ReturnType<
  typeof graphql.scalar<"CollectionItemsSortBy">
>[]

export type SortDirection = (typeof SortDirection)[number]
export const SortDirection = ["ASC", "DESC"] as const satisfies ReturnType<
  typeof graphql.scalar<"SortDirection">
>[]

export type Timeframe = (typeof Timeframe)[number]
export const Timeframe = [
  "ONE_MINUTE",
  "FIVE_MINUTE",
  "FIFTEEN_MINUTE",
  "ONE_DAY",
  "ONE_HOUR",
  "SEVEN_DAYS",
  "THIRTY_DAYS",
  "ONE_YEAR",
  "ALL_TIME",
] as const satisfies ReturnType<typeof graphql.scalar<"Timeframe">>[]

export type ProfileItemsSortBy = (typeof ProfileItemsSortBy)[number]

export const ProfileItemsSortBy = [
  "RECEIVED_DATE",
  "RARITY",
  "TOP_OFFER",
  "FLOOR_PRICE",
  "COST",
  "LISTING_OWN_PRICE",
  "LISTED_DATE",
  "CREATED_DATE",
  "LAST_SALE_PRICE",
] as const satisfies ReturnType<typeof graphql.scalar<"ProfileItemsSortBy">>[]

export type FungibleItemOrdersSortBy = ReturnType<
  typeof graphql.scalar<"FungibleItemOrdersSortBy">
>

export const FungibleItemOrdersSortBy = [
  "PRICE",
  "START_TIME",
  "END_TIME",
] as const satisfies FungibleItemOrdersSortBy[]

export type OrderSide = ReturnType<typeof graphql.scalar<"OrderSide">>
export const OrderSide = ["LISTING", "OFFER"] as const satisfies OrderSide[]

export type ChainArch = ReturnType<typeof graphql.scalar<"ChainArch">>

export const OrderStatus = ["ACTIVE", "INACTIVE"] as const satisfies ReturnType<
  typeof graphql.scalar<"OrderStatus">
>[]

export type OrderStatus = (typeof OrderStatus)[number]

export const ProfileOffersView = ["MADE", "RECEIVED"] as const

export type ProfileOffersView = (typeof ProfileOffersView)[number]

export const OfferStatus = [
  "NONE",
  "ACTIVE",
  "UNFUNDED",
  "COMPLETED",
  "EXPIRED",
  "CANCELLED",
] as const satisfies ReturnType<typeof graphql.scalar<"OfferStatus">>[]

export type OfferStatus = (typeof OfferStatus)[number]

export const ReceiptStatus = [
  "FAILED",
  "PARTIAL_SUCCESS",
  "PENDING",
  "SUCCESS",
] as const satisfies ReturnType<typeof graphql.scalar<"ReceiptStatus">>[]

export type ReceiptStatus = (typeof ReceiptStatus)[number]

export type ItemRarityCategoryType = ReturnType<
  typeof graphql.scalar<"ItemRarityCategoryType">
>

export const ProfileOrdersSortBy = [
  "START_TIME",
  "END_TIME",
] as const satisfies ReturnType<typeof graphql.scalar<"ProfileOrdersSortBy">>[]
export type ProfileOrdersSortBy = (typeof ProfileOrdersSortBy)[number]

export const ProfileOffersReceivedSortBy = [
  "START_TIME",
  "TOP_ASSET_OFFER",
] as const satisfies ReturnType<
  typeof graphql.scalar<"ProfileOffersReceivedSortBy">
>[]

export type ProfileOffersReceivedSortBy =
  (typeof ProfileOffersReceivedSortBy)[number]

export type DropStageType = ReturnType<typeof graphql.scalar<"DropStageType">>

export const DropStageType = [
  "MERKLE_PRESALE",
  "PUBLIC_SALE",
  "SIGNED_PRESALE",
] as const satisfies DropStageType[]

export type CollectionOwnershipCountSortBy = ReturnType<
  typeof graphql.scalar<"CollectionOwnershipCountSortBy">
>
export const CollectionOwnershipCountSortBy = [
  "TOTAL_QUANTITY",
] as const satisfies CollectionOwnershipCountSortBy[]

export type CollectionAttributeSortBy = ReturnType<
  typeof graphql.scalar<"CollectionAttributeSortBy">
>

export const CollectionAttributeSortBy = [
  "TOP_OFFER",
  "FLOOR_PRICE",
  "TRAIT_COUNT",
] as const satisfies CollectionAttributeSortBy[]

export type FavoriteItemsSortBy = (typeof FavoriteItemsSortBy)[number]

export const FavoriteItemsSortBy = [
  "CREATED_DATE",
  "PRICE",
  "RARITY",
  "LISTING_CREATED_DATE",
  "LAST_SALE",
] as const satisfies ReturnType<typeof graphql.scalar<"FavoriteItemsSortBy">>[]

export type ShelfIcon = ReturnType<typeof graphql.scalar<"ShelfIcon">>

export type ShelfView = ReturnType<typeof graphql.scalar<"ShelfView">>

export type SingleItemShelfSide = ReturnType<
  typeof graphql.scalar<"SingleItemShelfSide">
>

export const QuestTier = [
  "LEGENDARY",
  "EPIC",
  "RARE",
  "UNCOMMON",
  "COMMON",
] as const satisfies ReturnType<typeof graphql.scalar<"QuestTier">>[]

export type QuestTier = (typeof QuestTier)[number]

export const SocialNetwork = ["X", "DISCORD"] as const satisfies ReturnType<
  typeof graphql.scalar<"SocialNetwork">
>[]

export type SocialNetwork = (typeof SocialNetwork)[number]

export type EditableCollectionsStatus =
  (typeof EditableCollectionsStatus)[number]

export const EditableCollectionsStatus = [
  "PUBLISHED",
  "DRAFT",
  "MINTING",
  "MINT_ENDED",
] as const satisfies ReturnType<
  typeof graphql.scalar<"EditableCollectionsStatus">
>[]

export type EditableCollectionsType = (typeof EditableCollectionsType)[number]

export const EditableCollectionsType = [
  "COLLECTION",
  "DROP",
] as const satisfies ReturnType<
  typeof graphql.scalar<"EditableCollectionsType">
>[]

export type StudioContractStandard = ReturnType<
  typeof graphql.scalar<"ContractStandard">
>

export const StudioContractStandard = [
  "ERC721",
  "ERC1155",
] as const satisfies StudioContractStandard[]

export type EditableCollectionsSortBy =
  (typeof EditableCollectionsSortBy)[number]
export const EditableCollectionsSortBy = [
  "CREATED_DATE",
  "PUBLISHED_DATE",
] as const satisfies ReturnType<
  typeof graphql.scalar<"EditableCollectionsSortBy">
>[]

export type DropRevealState = (typeof DropRevealState)[number]
export const DropRevealState = [
  "ALREADY_REVEALED",
  "DROP_NOT_ENDED",
  "NEEDS_MORE_MEDIA",
  "READY_TO_REPUBLISH",
  "READY_TO_REVEAL",
] as const satisfies ReturnType<typeof graphql.scalar<"DropRevealState">>[]

export type WorkflowExecutionStatus = (typeof WorkflowExecutionStatus)[number]
export const WorkflowExecutionStatus = [
  "NOT_FOUND",
  "COMPLETED",
  "RUNNING",
  "FAILED",
] as const satisfies ReturnType<
  typeof graphql.scalar<"WorkflowExecutionStatus">
>[]

export type TsState = ReturnType<typeof graphql.scalar<"TsState">>
export type SwapProviderType = ReturnType<
  typeof graphql.scalar<"SwapProviderType">
>
export type SwapReceiptFailReason = ReturnType<
  typeof graphql.scalar<"SwapReceiptFailReason">
>

export const CurrencyWarningFeatureId = [
  "UNSELLABLE_TOKEN",
  "UNSTABLE_TOKEN_PRICE",
  "WASH_TRADING",
  "AIRDROP_PATTERN",
  "CONCENTRATED_SUPPLY_DISTRIBUTION",
  "DYNAMIC_ANALYSIS",
  "FAKE_TRADE_MAKER_COUNT",
  "FAKE_VOLUME",
  "HIDDEN_SUPPLY_BY_KEY_HOLDER",
  "HIGH_BUY_FEE",
  "HIGH_SELL_FEE",
  "HIGH_TRANSFER_FEE",
  "HONEYPOT",
  "IMPERSONATOR",
  "INAPPROPRIATE_CONTENT",
  "INORGANIC_VOLUME",
  "INSUFFICIENT_LOCKED_LIQUIDITY",
  "KNOWN_MALICIOUS",
  "METADATA",
  "RUGPULL",
  "SANCTIONED_CREATOR",
  "SPAM_TEXT",
  "STATIC_CODE_SIGNATURE",
] as const satisfies ReturnType<
  typeof graphql.scalar<"CurrencyWarningFeatureId">
>[]
export type CurrencyWarningFeatureId = (typeof CurrencyWarningFeatureId)[number]

export type CandleBucketSize = ReturnType<
  typeof graphql.scalar<"CandleBucketSize">
>
export const CandleBucketSize = [
  "ONE_MINUTE",
  "FIVE_MINUTES",
  "FIFTEEN_MINUTES",
  "ONE_HOUR",
  "FOUR_HOURS",
  "ONE_DAY",
] as const satisfies CandleBucketSize[]

export type SwapProtocolIdentifier = ReturnType<
  typeof graphql.scalar<"SwapProtocolIdentifier">
>
export const SwapProtocolIdentifier = [
  "orca",
  "raydium",
  "uniswap",
  "pancake_swap",
  "aerodrome",
  "pumpfun",
  "meteora",
  "opensea",
] as const satisfies SwapProtocolIdentifier[]

export type ProfileActivityType = (typeof ProfileActivityType)[number]
export const ProfileActivityType = [
  "SWAP",
  "SEND",
  "RECEIVE",
] as const satisfies ReturnType<typeof graphql.scalar<"ProfileActivityType">>[]
